// =============================================================================
// Project Listings (Partner Opportunities) — data & smart templates
// =============================================================================
// Slugs MUST stay in sync with PROJECT_LISTING_TYPES, PROJECT_LISTING_DIVISIONS,
// PROJECT_LISTING_STATUSES, and PROJECT_PARTNER_APP_STATUSES in
// /app/backend/models.py.
// =============================================================================

export const PROJECT_TYPES = [
  { slug: "residential", label: "Residential" },
  { slug: "commercial", label: "Commercial" },
  { slug: "roofing", label: "Roofing" },
  { slug: "remodeling", label: "Remodeling" },
  { slug: "maintenance", label: "Maintenance" },
  { slug: "exterior", label: "Exterior" },
  { slug: "investor-rental", label: "Investor / Rental" },
  { slug: "multi-trade", label: "Multi-trade" },
  { slug: "single-division", label: "Single Division" },
  { slug: "emergency-storm", label: "Emergency / Storm" },
];

export const PROJECT_TYPE_LABEL = Object.fromEntries(
  PROJECT_TYPES.map((t) => [t.slug, t.label])
);

export const PROJECT_DIVISIONS = [
  { slug: "demo", label: "Demo" },
  { slug: "roofing", label: "Roofing" },
  { slug: "framing", label: "Framing" },
  { slug: "drywall", label: "Drywall" },
  { slug: "painting", label: "Painting" },
  { slug: "flooring", label: "Flooring" },
  { slug: "tile", label: "Tile" },
  { slug: "electrical", label: "Electrical" },
  { slug: "plumbing", label: "Plumbing" },
  { slug: "hvac", label: "HVAC" },
  { slug: "act-ceiling-grid", label: "ACT / Ceiling Grid" },
  { slug: "doors-hardware", label: "Doors / Hardware" },
  { slug: "windows", label: "Windows" },
  { slug: "siding-trim", label: "Siding / Trim" },
  { slug: "fascia-soffit", label: "Fascia / Soffit" },
  { slug: "gutters-drainage", label: "Gutters / Drainage" },
  { slug: "fencing", label: "Fencing" },
  { slug: "concrete", label: "Concrete" },
  { slug: "masonry", label: "Masonry" },
  { slug: "landscaping", label: "Landscaping" },
  { slug: "exterior-repairs", label: "Exterior Repairs" },
  { slug: "interior-feature-upgrades", label: "Interior Feature Upgrades" },
  { slug: "maintenance-punch-list", label: "Maintenance / Punch List" },
  { slug: "final-clean", label: "Final Clean" },
  { slug: "specialty-shielding-imaging", label: "Specialty Shielding / Imaging Room Support" },
  { slug: "other", label: "Other" },
];

export const DIVISION_LABEL = Object.fromEntries(
  PROJECT_DIVISIONS.map((d) => [d.slug, d.label])
);

export const PROJECT_LISTING_STATUSES = [
  { slug: "open", label: "Open", tone: "emerald" },
  { slug: "reviewing-partners", label: "Reviewing Partners", tone: "amber" },
  { slug: "walkthrough-scheduled", label: "Walkthrough Scheduled", tone: "violet" },
  { slug: "awarded", label: "Awarded", tone: "sky" },
  { slug: "closed", label: "Closed", tone: "gray" },
];

export const PROJECT_LISTING_STATUS_LABEL = Object.fromEntries(
  PROJECT_LISTING_STATUSES.map((s) => [s.slug, s.label])
);

export const PARTNER_APP_STATUSES = [
  { slug: "new", label: "New", tone: "slate" },
  { slug: "reviewing", label: "Reviewing", tone: "amber" },
  { slug: "needs-documents", label: "Needs Documents", tone: "red" },
  { slug: "site-walk-invited", label: "Site Walk Invited", tone: "violet" },
  { slug: "shortlisted", label: "Shortlisted", tone: "sky" },
  { slug: "selected", label: "Selected", tone: "emerald" },
  { slug: "not-selected", label: "Not Selected", tone: "red" },
  { slug: "archived", label: "Archived", tone: "gray" },
];

export const URGENCY_OPTIONS = ["Standard", "Priority", "Urgent", "Emergency"];
export const MATERIALS_OPTIONS = ["Yes", "No", "Partial"];
export const LICENSE_OPTIONS = ["Yes", "No", "If applicable"];
export const OCCUPIED_OPTIONS = ["Occupied", "Vacant", "Phased", "Unsure"];
export const CONTACT_METHODS = ["Phone call", "Email", "Text"];

// -----------------------------------------------------------------------
// Smart template — the admin's "Generate from template" button splats the
// matching block into the listing form based on the selected project type +
// division. Admin can edit any field before publishing.
// -----------------------------------------------------------------------
function tplOverview(typeLabel, divisionLabel, city) {
  const where = city ? ` in the ${city} area` : " in the Austin area";
  return `Black Ridge Construction is looking for a qualified ${divisionLabel.toLowerCase()} partner for an upcoming ${typeLabel.toLowerCase()} project${where}.`;
}

function tplScopeNeeded(divisionLabel) {
  const d = divisionLabel.toLowerCase();
  const scopes = {
    drywall: "This opportunity may include drywall repair, texture matching, interior prep, priming, painting, touch-ups, and clean finish work. Final scope will be confirmed after review and site coordination.",
    painting: "This opportunity may include interior or exterior painting, prep, priming, masking, color matching, and clean finish work. Final scope will be confirmed after review and site coordination.",
    roofing: "This opportunity may include roof tear-off, underlayment, flashing, shingle install, valleys, penetrations, and final cleanup. Confirm scope and access notes after site coordination.",
    framing: "This opportunity may include rough carpentry, blocking, backing, structural framing adjustments, and prep for follow-on trades.",
    flooring: "This opportunity may include subfloor prep, underlayment, install of LVP / hardwood / tile, transitions, and finish details.",
    tile: "This opportunity may include tile install, substrate prep, waterproofing, grout, and finish detail work.",
    electrical: "This opportunity may include licensed electrical work coordinated through qualified partners. Verified licensing and insurance required.",
    plumbing: "This opportunity may include licensed plumbing work coordinated through qualified partners. Verified licensing and insurance required.",
    hvac: "This opportunity may include licensed HVAC work coordinated through qualified partners. Verified licensing and insurance required.",
    demo: "This opportunity may include selective interior demolition, removal of finishes, debris removal, and prep for the next trade.",
    "act-ceiling-grid": "This opportunity may include ACT grid install, tile install, light box coordination, and finish work.",
    "siding-trim": "This opportunity may include siding and trim repair or install, fascia/soffit work, sealant, and exterior finish details.",
    "exterior-repairs": "This opportunity may include exterior carpentry repairs, trim, siding, fascia, soffit, sealant, paint touch-ups, and miscellaneous exterior items.",
    "gutters-drainage": "This opportunity may include gutter install or repair, downspout work, splash blocks, and drainage-related improvements.",
    fencing: "This opportunity may include fence install, repair, gate work, posts, and finish details.",
    "specialty-shielding-imaging": "This opportunity may include imaging center coordination, shielding-related removal/install support, demo, framing adjustments, drywall, paint, ACT, and tile. Specialty trades verified separately.",
  };
  return scopes[d] || `This opportunity may include scope-specific ${d} work. Final scope will be confirmed after review and site coordination.`;
}

const BASE_REQUIREMENTS = [
  "Reliable communication, professional crew presentation, and clean workmanship",
  "Active general liability insurance when required for the scope",
  "Own tools, transportation, and crew safety equipment",
  "Ability to meet agreed production timelines and provide clear progress updates",
  "Comfort with photo-documented work and basic written reporting",
];

function tplRequirements(division) {
  const extras = {
    electrical: "• Verified electrical license in good standing",
    plumbing: "• Verified plumbing license in good standing",
    hvac: "• Verified HVAC license in good standing",
    roofing: "• Proven roofing crew experience, fall-protection compliance, and active insurance",
    "specialty-shielding-imaging": "• Experience supporting medical / imaging spaces and clean working environments",
  };
  const lines = BASE_REQUIREMENTS.map((r) => `• ${r}`);
  if (extras[division]) lines.push(extras[division]);
  return lines.join("\n");
}

export function generateProjectListingTemplate({ project_type, division, city, urgency }) {
  const typeLabel = PROJECT_TYPE_LABEL[project_type] || "Construction";
  const divisionLabel = DIVISION_LABEL[division] || "Specialty";
  return {
    title: `${divisionLabel} Partner Needed — ${typeLabel}${city ? ` (${city})` : ""}`,
    overview: tplOverview(typeLabel, divisionLabel, city),
    scope_needed: tplScopeNeeded(divisionLabel),
    requirements: tplRequirements(division),
    timeline_notes: urgency && urgency !== "Standard"
      ? `Timing: ${urgency.toLowerCase()} response preferred. Final start date and duration will be confirmed during partner review.`
      : "Timing: standard scheduling. Final start date and duration will be confirmed during partner review.",
    location_notes: city
      ? `Location: ${city} area. Specific access notes and site walk timing shared with shortlisted partners.`
      : "Location: Austin-area project. Specific access notes and site walk timing shared with shortlisted partners.",
  };
}
